import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import type { SyntheticEvent } from 'react'
import { useState } from 'react'

import { canListStoreUids, useGetStoreUidsQuery } from 'api/canonicalPicker'
import type { StoreUidRow, UidGroupRow } from 'api/canonicalPicker.types'
import PickerRowLabel from 'components/AI/CanonicalPicker/PickerRowLabel'
import RetryAlert from 'components/AI/CanonicalPicker/RetryAlert'
import TreeGuide from 'components/AI/CanonicalPicker/TreeGuide'
import {
  subRowStyleOf,
  UID_ROW_STYLE,
} from 'components/AI/CanonicalPicker/uidRowStyles'
import palette from 'constants/palette'
import { Icon, MultiSelectOption } from 'controlled-components'
import { buildTranslate } from 'locales'
import type { PickerSource } from 'slices/aiConversations/pickerTypes'
import type { SourceSelection } from 'slices/aiSegmentBuilder/picker'
import { generateId } from 'utilities/uniqueId'

import { groupStateOf } from './uidTree'

const t = buildTranslate('components.ai.picker.canonical.store_uids')

const CHEVRON_SIZE = 16
const SPINNER_SIZE = 16
// Lines a sub-row's status up with its checkbox: row padding, tree guide and gap
const SUB_ROW_INDENT = '50px'
const HIGHLIGHT_BAR_WIDTH = '3px'

type Props = {
  source: PickerSource
  group: UidGroupRow
  selection: SourceSelection
  locationNames: ReadonlyMap<number, string>
  locationsFailed: boolean
  isExpanded: boolean
  /** UIDs the merchant searched for, whose rows are marked */
  highlightedUids: ReadonlySet<string>
  /** The element that says what a global UID covers */
  globalDescriptionId: string
  onToggleExpanded: () => void
  onPickGroup: () => void
  onPickStoreUid: (row: StoreUidRow) => void
}

/**
 * A UID group in a fixed list. A global one opens to its store UIDs, read
 * only once opened. Picking the group covers them all, so they show ticked
 * and can't be picked one by one; picking some of them leaves it mixed.
 */
const UidGroupOptionsContainer = ({
  source,
  group,
  selection,
  locationNames,
  locationsFailed,
  isExpanded,
  highlightedUids,
  globalDescriptionId,
  onToggleExpanded,
  onPickGroup,
  onPickStoreUid,
}: Props) => {
  const [css] = useStyletron()
  const [subRowsId] = useState(() => `store-uids-${generateId()}`)
  // A group read without its store UIDs has nothing to ask the details source for
  const canExpand =
    group.isGlobal && group.storeUids !== null && canListStoreUids(source)
  const isOpen = canExpand && isExpanded
  const { currentData, isError, isFetching, refetch } = useGetStoreUidsQuery(
    { source, group },
    { skip: !isOpen }
  )
  const state = groupStateOf(group, selection)
  const isGroupPicked = state === 'picked'
  const highlightOf = (uid: string) =>
    highlightedUids.has(uid)
      ? css({
          borderLeft: `${HIGHLIGHT_BAR_WIDTH} solid ${palette.eggYolk50}`,
        })
      : undefined
  const statusClassName = `py-s ${css({
    paddingLeft: SUB_ROW_INDENT,
    backgroundColor: palette.grey05,
  })}`

  const renderSubRows = () => {
    if (isError) {
      return (
        <div className={`pr-m ${statusClassName}`}>
          <RetryAlert
            message={t('error', { name: group.name })}
            disabled={false}
            isLoading={isFetching}
            onRetry={refetch}
          />
        </div>
      )
    }
    if (!currentData) {
      return (
        <div role="status" className={statusClassName}>
          <Icon name="spinner" size={SPINNER_SIZE} spin aria-hidden="true" />
          <span className="sr-only">{t('loading', { name: group.name })}</span>
        </div>
      )
    }
    if (currentData.length === 0) {
      return (
        <div role="status" className={statusClassName}>
          <Text.BodyText5 color="grey70">{t('empty')}</Text.BodyText5>
        </div>
      )
    }
    return currentData.map(row => {
      const isPicked = isGroupPicked || row.id in selection.rows
      return (
        <MultiSelectOption
          key={row.id}
          value={row.id}
          selected={isPicked}
          disabled={isGroupPicked}
          overrides={subRowStyleOf(isPicked)}
          className={highlightOf(row.uid)}
          leading={<TreeGuide />}
          onSelect={() => onPickStoreUid(row)}
        >
          <PickerRowLabel
            row={row}
            locationNames={locationNames}
            locationsFailed={locationsFailed}
            isSubRow
          />
        </MultiSelectOption>
      )
    })
  }

  return (
    <>
      <MultiSelectOption
        value={group.id}
        selected={isGroupPicked}
        indeterminate={state === 'mixed'}
        overrides={UID_ROW_STYLE}
        className={highlightOf(group.uidGroup)}
        aria-describedby={group.isGlobal ? globalDescriptionId : undefined}
        onSelect={onPickGroup}
      >
        <PickerRowLabel
          row={group}
          locationNames={locationNames}
          locationsFailed={locationsFailed}
        >
          {canExpand && (
            <Button
              kind="minimal"
              size="small"
              className="p-0"
              aria-label={t('toggle', { name: group.name })}
              aria-expanded={isOpen}
              aria-controls={subRowsId}
              onClick={(event: SyntheticEvent) => {
                // The chevron sits inside the option, so its click must not pick the group
                event.stopPropagation()
                onToggleExpanded()
              }}
            >
              <Icon
                name={isOpen ? 'chevron-down' : 'chevron-right'}
                size={CHEVRON_SIZE}
                aria-hidden="true"
              />
            </Button>
          )}
        </PickerRowLabel>
      </MultiSelectOption>
      {isOpen && (
        <div
          role="group"
          id={subRowsId}
          aria-label={t('toggle', { name: group.name })}
        >
          {renderSubRows()}
        </div>
      )}
    </>
  )
}

export default UidGroupOptionsContainer
